import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Plus, Pencil } from "lucide-react"
import { toast } from "sonner"
import { api, type ApiEnvelope, type MenuItem } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FieldError } from "@/components/ui/field-error"
import { Switch } from "@/components/ui/switch"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { ConfirmDelete } from "@/components/admin/confirm-delete"

export const Route = createFileRoute("/admin/_authed/menu")({
  component: MenuPage,
})

type FormState = { label: string; url: string; order: number; is_active: boolean; is_external: boolean }
const empty: FormState = { label: "", url: "", order: 0, is_active: true, is_external: false }

function MenuPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<MenuItem[]>({
    queryKey: ["admin", "menu-items"],
    queryFn: async () => (await api.get<ApiEnvelope<MenuItem[]>>("/admin/menu-items")).data.data,
  })

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<MenuItem | null>(null)
  const [form, setForm] = useState<FormState>(empty)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "menu-items"] })
    queryClient.invalidateQueries({ queryKey: ["menu-items"] })
  }

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        await api.put(`/admin/menu-items/${editing.id}`, form)
      } else {
        await api.post("/admin/menu-items", form)
      }
    },
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setFieldErrors({})
      toast.success(editing ? "Menu item updated." : "Menu item created.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/admin/menu-items/${id}`),
    onSuccess: () => {
      invalidate()
      toast.success("Menu item deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(empty)
    setFieldErrors({})
    setOpen(true)
  }
  const openEdit = (item: MenuItem) => {
    setEditing(item)
    setForm({
      label: item.label,
      url: item.url,
      order: item.order,
      is_active: item.is_active,
      is_external: item.is_external,
    })
    setFieldErrors({})
    setOpen(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Menu</h1>
          <p className="mt-1 text-sm text-muted-foreground">Navigation items shown in the site header.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full">
              <Plus className="h-4 w-4" /> New item
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit menu item" : "New menu item"}</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="label">Label</Label>
                  <Input
                    id="label"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    aria-invalid={!!fieldErrors.label}
                  />
                  <FieldError message={fieldErrors.label} />
                </div>
                <div>
                  <Label htmlFor="url">URL</Label>
                  <Input
                    id="url"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    aria-invalid={!!fieldErrors.url}
                  />
                  <FieldError message={fieldErrors.url} />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
                <div>
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  />
                </div>
                <div className="flex items-center gap-3 pb-2">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                    id="is_active"
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.is_external}
                  onCheckedChange={(v) => setForm({ ...form, is_external: v })}
                  id="is_external"
                />
                <Label htmlFor="is_external">External link</Label>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-card">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="p-6 text-sm text-destructive">Couldn't load menu items.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Label</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>External</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data ?? []).map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.label}</TableCell>
                  <TableCell className="text-muted-foreground">{item.url}</TableCell>
                  <TableCell>{item.order}</TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? "default" : "secondary"}>
                      {item.is_active ? "Active" : "Hidden"}
                    </Badge>
                  </TableCell>
                  <TableCell>{item.is_external ? "Yes" : "No"}</TableCell>
                  <TableCell className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(item)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <ConfirmDelete itemLabel={item.label} onConfirm={() => remove.mutate(item.id)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
